import { useRef, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconCamera } from '@tabler/icons-react';
import type { AuthSessionUser } from '@domain/auth/types';
import type { UserProfile, UserProfileUpdateInput } from '@domain/profile/types';
import { type MediaLocator } from '@domain/media/resolve';
import { initials } from '@shared/utils/ui';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { InlineEditField } from './InlineEditField';

export type ProfileDraft = UserProfileUpdateInput & {
  displayName: string;
  email: string;
  phone: string;
  signature: string;
  avatarAssetId: string;
  homepageBackgroundAssetId: string;
  avatar: MediaLocator | null;
  homepageBackground: MediaLocator | null;
};

export type ProfileImageField = 'avatar' | 'homepageBackground';

function ProfileAccountDetail({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Group className="inline-edit-row" align="flex-start" gap="md" wrap="nowrap">
      <Text size="sm" fw={600} className="inline-edit-label">{label}</Text>
      <Text size="sm" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
        {value || '未绑定'}
      </Text>
    </Group>
  );
}

export function createProfileDraft(
  user: AuthSessionUser | null,
  profile?: Partial<UserProfile>,
): ProfileDraft {
  return {
    displayName: profile?.displayName ?? user?.displayName ?? '',
    email: profile?.email ?? user?.email ?? '',
    phone: profile?.phone ?? '',
    avatarAssetId: profile?.avatar?.assetId ?? '',
    avatar: profile?.avatar ?? null,
    signature: profile?.signature ?? '',
    homepageBackgroundAssetId: profile?.homepageBackground?.assetId ?? '',
    homepageBackground: profile?.homepageBackground ?? null,
  };
}

type ProfileSettingsFormProps = {
  currentUser: AuthSessionUser;
  draft: ProfileDraft;
  fallbackAvatarUrl?: string;
  loadState: 'idle' | 'loading' | 'saving' | 'error';
  error: string | null;
  dirty: boolean;
  cdnBase: string;
  uploadingField: ProfileImageField | null;
  onChange: (field: keyof ProfileDraft, value: string) => void;
  onPickImage: (field: ProfileImageField, file: File) => void;
  onSave: () => void;
  onReset: () => void;
};

export function ProfileSettingsForm({
  currentUser,
  draft,
  fallbackAvatarUrl,
  loadState,
  error,
  dirty,
  cdnBase,
  uploadingField,
  onChange,
  onPickImage,
  onSave,
}: ProfileSettingsFormProps) {
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const readFile = (field: ProfileImageField) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file) {
      onPickImage(field, file);
    }
  };

  return (
    <Stack gap={30}>
      <Stack gap="lg">
        <Group justify="center" className="profile-avatar-picker">
          <UnstyledButton
            className="profile-avatar-picker__button"
            aria-label="更换头像"
            aria-busy={uploadingField === 'avatar'}
            title="点击更换头像"
            disabled={uploadingField === 'avatar'}
            onClick={() => avatarInputRef.current?.click()}
          >
            <UserAvatar
              className="profile-avatar-picker__image"
              id={currentUser.id}
              name={draft.displayName || currentUser.displayName}
              fallback={initials(draft.displayName || currentUser.displayName || currentUser.email)}
              media={draft.avatar}
              avatarUrl={draft.avatar ? undefined : fallbackAvatarUrl}
              cdnBase={cdnBase}
              size={88}
            />
            <Box className="profile-avatar-picker__badge" aria-hidden="true">
              {uploadingField === 'avatar'
                ? <Loader size={14} color="white" />
                : <IconCamera size={15} stroke={2.2} />}
            </Box>
          </UnstyledButton>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={readFile('avatar')}
          />
        </Group>
        <InlineEditField
          label="昵称"
          value={draft.displayName}
          placeholder="你的名字"
          onChange={(val) => onChange('displayName', val)}
        />
        <InlineEditField
          label="个性签名"
          value={draft.signature}
          placeholder="介绍一下你自己"
          multiline
          onChange={(val) => onChange('signature', val)}
        />
        <Stack gap="lg">
          <ProfileAccountDetail label="登录邮箱" value={draft.email} />
          <ProfileAccountDetail label="手机号码" value={draft.phone} />
        </Stack>
      </Stack>

      {loadState === 'loading' && <Alert color="terracotta" variant="light">正在加载资料…</Alert>}
      {error && <Alert color="red" variant="light">{error}</Alert>}

      {dirty && loadState !== 'loading' && (
        <Group justify="center">
          <Button onClick={onSave} loading={loadState === 'saving'}>
            保存资料
          </Button>
        </Group>
      )}
    </Stack>
  );
}
